import { IApiErrorResponse, callApi } from '@/api/callApi';
import { useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from 'reactstrap';

import {
  IAddEarningModalProps,
  IAddSalaryEarningsForm,
} from '@/interfaces/salary.model';
import { showToastNotification } from '@/utility/helper';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { IDeleteResponse } from '@/interfaces/common.model';
import ErrorMessage from '../common/ErrorMessage';
import { errorTypes } from '@/utility/validation';

const AddDeductionModal = ({
  modal,
  toggle,
  deductionsList,
  selectedSalaryHeads,
  selectedSiteId,
  getSalaryHead,
  getSiteSalaryHeadDetail,
  modalData,
  setModalData,
}: IAddEarningModalProps) => {
  // consoa>>>>>.", modalData);

  const [isLoading, setIsLoading] = useState(false);
  const methods = useForm<IAddSalaryEarningsForm>();
  const {
    handleSubmit,
    reset,
    register,
    setValue,
    watch,
    formState: { errors },
  } = methods;
  const isFixed = watch('isFixed');
  const isConditionBased = watch('isConditionBased');

  useEffect(() => {
    if (modalData && Object.keys(modalData)?.length > 0) {
      setTimeout(() => {
        reset({
          salaryHeadId: modalData?.salaryHeadId,
          amount: modalData?.amount || '', // Only if isFixed is true
          isFixed: modalData?.isFixed,
          fixedBasedOn: modalData?.fixedBasedOn || '',
          rate: modalData?.rate || '',
          maxAmount: modalData?.maxAmount || '',
          isConditionBased: modalData?.isConditionBased ?? false,
          conditionBasedOn: modalData?.conditionBasedOn || '',
          condition: modalData?.condition || '',
          value: modalData?.value || '',
        });
      }, 200);
    } else {
      reset({
        salaryHeadId: '',
        amount: '',
        isFixed: true,
        fixedBasedOn: '',
        rate: '',
        maxAmount: '',
        isConditionBased: false,
        conditionBasedOn: '',
        condition: '',
        value: '',
      });
    }
  }, [modalData, reset]);

  const closeHandler = () => {
    setModalData(null);
    toggle();
    reset();
  };

  const onSubmit: SubmitHandler<IAddSalaryEarningsForm> = async (data) => {
    const isUpdate = modalData && Object.keys(modalData)?.length > 0;

    let submitData: IAddSalaryEarningsForm = {
      siteId: selectedSiteId,
      salaryHeadId: data?.salaryHeadId,
      type: 'deduction',
      isFixed: data?.isFixed,
      isConditionBased: data?.isConditionBased,
    };

    if (isUpdate) {
      if (data?.isFixed) {
        submitData.amount = data?.amount || '';
        submitData.fixedBasedOn = null;
        submitData.rate = null;
        submitData.maxAmount = null;
      } else {
        submitData.amount = null;
        submitData.fixedBasedOn = data?.fixedBasedOn || '';
        submitData.rate = data?.rate || '';
        submitData.maxAmount = data?.maxAmount || '';
      }

      if (data?.isConditionBased) {
        submitData.conditionBasedOn = data?.conditionBasedOn || '';
        submitData.condition = data?.condition || '';
        submitData.value = data?.value || '';
      } else {
        submitData.conditionBasedOn = null;
        submitData.condition = null;
        submitData.value = null;
      }
    } else {
      submitData = {
        ...submitData,
        ...(data?.amount && { amount: data?.amount }),
        ...(data?.fixedBasedOn && { fixedBasedOn: data?.fixedBasedOn }),
        ...(data?.rate && { rate: data?.rate }),
        ...(data?.maxAmount && { maxAmount: data?.maxAmount }),
        ...(data?.conditionBasedOn && {
          conditionBasedOn: data?.conditionBasedOn,
        }),
        ...(data?.condition && { condition: data?.condition }),
        ...(data?.value && { value: data?.value }),
      };
    }
    // const options = {
    //   method: 'POST',
    //   data: submitData,
    // };
    // let API =
    //   modalData && Object.keys(modalData)?.length > 0
    //     ? apiUrl?.update_site_salary_head + `?siteSalaryHeadId=${modalData?.id}`
    //     : apiUrl?.create_site_salary_head;
    try {
      setIsLoading(true);
      const response: IDeleteResponse = await callApi(
        isUpdate
          ? API_URL +
              API_ENDPOINT.updatesSitesSalaryHead +
              `?siteSalaryHeadId=${modalData?.id}`
          : API_URL + API_ENDPOINT.createSiteSalaryHead,
        {
          method: 'POST',
          data: submitData,
        },
      );
      setIsLoading(false);
      if (response?.status === true) {
        showToastNotification('success', response?.message);
        closeHandler();
        getSiteSalaryHeadDetail();
        getSalaryHead('deduction');
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    }
  };

  const basicAndGrossSalaries = selectedSalaryHeads?.earning?.filter(
    (item) => item?.salaryhead?.canAppearInBasedOn === true,
  );

  return (
    <Modal
      className=""
      isOpen={modal}
      toggle={closeHandler}
      size="md"
      scrollable={true}
      centered
    >
      <ModalHeader toggle={closeHandler}>
        <h3>
          {modalData && Object.keys(modalData)?.length > 0 ? 'Update' : 'Add'}{' '}
          Deduction
        </h3>
      </ModalHeader>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody
            className="p-4"
            style={{ maxHeight: '80vh', overflowY: 'auto' }}
          >
            <div className="modal-content">
              <div className="row">
                {/* Title Dropdown */}
                <div className="col-12">
                  <div className="form-group mb-3">
                    <label htmlFor="erning" className="form-label">
                      Title <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select mb-3"
                      {...register('salaryHeadId', {
                        required: 'This field is Required',
                      })}
                    >
                      <option value="">Select Deduction</option>
                      {deductionsList?.map((item) => {
                        const isSelected = selectedSalaryHeads?.deduction?.some(
                          (selected) => selected?.salaryhead?.id === item?.id,
                        );
                        return (
                          <option
                            key={item.id}
                            value={item.id}
                            disabled={isSelected}
                          >
                            {item.name}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <ErrorMessage message={errors?.salaryHeadId?.message} />
                </div>

                {/* Is Fixed Toggle */}
                <div className="col-12">
                  <div className="form-group mb-3">
                    <label htmlFor="isFixed" className="form-label">
                      Fixed
                    </label>
                    <div className="form-check form-switch fs-2 mx-2">
                      <input
                        type="checkbox"
                        className="form-check-input rounded-5"
                        id="isFixed"
                        checked={!!isFixed}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setValue('isFixed', checked);

                          if (checked) {
                            setValue('fixedBasedOn', '');
                            setValue('rate', '');
                            setValue('maxAmount', '');
                          } else {
                            setValue('amount', '');
                          }
                        }}
                      />
                    </div>
                  </div>
                  <ErrorMessage message={errors?.isFixed?.message} />
                </div>

                {/* Conditionally Rendered Fields */}
                {isFixed ? (
                  // If IsFixed is TRUE, show Amount
                  <div className="col-12">
                    <div className="form-group mb-3">
                      <label htmlFor="amt" className="form-label">
                        Amount <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        onWheel={(e) => (e.target as HTMLInputElement).blur()}
                        className="form-control"
                        placeholder="Amount"
                        step="any"
                        min={1}
                        onInput={(e) => {
                          e.currentTarget.value = e.currentTarget.value.replace(
                            /-/g,
                            '',
                          );
                        }}
                        {...register('amount', {
                          required: 'This field is Required',
                        })}
                      />
                    </div>
                    <ErrorMessage message={errors?.amount?.message} />
                  </div>
                ) : (
                  // If IsFixed is FALSE, show BasedOn, Rate, MaxAmount
                  <>
                    <div className="col-12">
                      <div className="form-group mb-3">
                        <label htmlFor="basedOn" className="form-label">
                          Based On <span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-select mb-3"
                          {...register('fixedBasedOn', {
                            required: 'This field is Required',
                          })}
                        >
                          <option value="">Select Based On</option>
                          {basicAndGrossSalaries.length > 0 ? (
                            basicAndGrossSalaries?.map((item) => (
                              <option key={item?.id} value={item?.id}>
                                {item?.salaryhead?.name}
                              </option>
                            ))
                          ) : (
                            <option disabled>No data found</option>
                          )}
                        </select>
                      </div>
                      <ErrorMessage message={errors?.fixedBasedOn?.message} />
                    </div>

                    <div className="col-12">
                      <div className="form-group mb-3">
                        <label htmlFor="rate" className="form-label">
                          Rate (In Percentage)
                          <span className="text-danger">*</span>
                        </label>
                        <input
                          type="number"
                          onWheel={(e) => (e.target as HTMLInputElement).blur()}
                          className="form-control"
                          placeholder="Rate"
                          step="any"
                          min={0}
                          max={100}
                          {...register('rate', {
                            required: 'This field is Required',
                          })}
                        />
                      </div>
                      <ErrorMessage message={errors?.rate?.message} />
                    </div>

                    <div className="col-12">
                      <div className="form-group mb-3">
                        <label htmlFor="maxAmount" className="form-label">
                          Maximum Amount <span className="text-danger">*</span>
                        </label>
                        <input
                          type="number"
                          onWheel={(e) => (e.target as HTMLInputElement).blur()}
                          className="form-control"
                          placeholder="Maximum Amount"
                          step="any"
                          min={0}
                          {...register('maxAmount', {
                            required: 'This field is Required',
                          })}
                        />
                      </div>
                      <ErrorMessage message={errors?.maxAmount?.message} />
                    </div>
                  </>
                )}
                <div className="col-12">
                  <div className="form-group mb-3">
                    <label htmlFor="isCondition" className="form-label">
                      Condition Based
                    </label>
                    <div className="form-check form-switch fs-2 mx-2">
                      <input
                        type="checkbox"
                        className="form-check-input rounded-5"
                        id="isCondition"
                        defaultChecked={false}
                        {...register('isConditionBased', {
                          onChange: () => {
                            setValue('conditionBasedOn', '');
                            setValue('condition', '');
                            setValue('value', '');
                          },
                        })}
                      />
                    </div>
                  </div>
                  <ErrorMessage message={errors?.isConditionBased?.message} />
                </div>
                {isConditionBased && (
                  <>
                    <div className="col-12 mt-2">
                      <div className="form-group mb-3">
                        <label htmlFor="basedOn" className="form-label">
                          Based On <span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-select mb-3"
                          {...register('conditionBasedOn', {
                            required: 'This field is Required',
                          })}
                        >
                          <option value="">Select Condition Based On</option>
                          {basicAndGrossSalaries.length > 0 ? (
                            basicAndGrossSalaries?.map((item) => (
                              <option key={item?.id} value={item?.id}>
                                {item?.salaryhead?.name}
                              </option>
                            ))
                          ) : (
                            <option disabled>No data found</option>
                          )}
                        </select>
                      </div>
                      <ErrorMessage
                        message={errors?.conditionBasedOn?.message}
                      />
                    </div>

                    <div className="col-12">
                      <div className="form-group mb-3">
                        <label htmlFor="condition" className="form-label">
                          Condition
                        </label>
                        <select
                          className="form-select form-control"
                          {...register('condition', {
                            required: 'This field is Required',
                          })}
                        >
                          <option value="">Select Condition</option>
                          <option value="==">Equal (==)</option>
                          <option value="!=">Not Equal (!=)</option>
                          <option value="<">Less Than (&lt;)</option>
                          <option value=">">Greater Than (&gt;)</option>
                          <option value="<=">
                            Less Than or Equal To (&lt;=)
                          </option>
                          <option value=">=">
                            Greater Than or Equal To (&gt;=)
                          </option>
                        </select>
                        <ErrorMessage message={errors?.condition?.message} />
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-group mb-3">
                        <label htmlFor="maxAmount" className="form-label">
                          Value <span className="text-danger">*</span>
                        </label>
                        <input
                          type="number"
                          onInput={(e) => {
                            e.currentTarget.value =
                              e.currentTarget.value.replace(/-/g, '');
                          }}
                          onWheel={(e) => (e.target as HTMLInputElement).blur()}
                          className="form-control"
                          placeholder="Value"
                          step="any"
                          min={1}
                          {...register('value', {
                            ...errorTypes.required(),
                            valueAsNumber: true,
                          })}
                        />
                      </div>
                      <ErrorMessage message={errors?.value?.message} />
                    </div>
                  </>
                )}
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={closeHandler}
            >
              Close
            </button>
            <button type="submit" className="btn btn-primary">
              {isLoading ? <Spinner /> : 'Save changes'}
            </button>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default AddDeductionModal;
